// src/store/itemSlice.ts

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getBaseUrl } from '../../Utils';
import { MetadataDrillThruSetApiDTO_MDS } from '../../Pages/MDSTool/MdsInterface/MdsInterface';

// Define the interface for the slice state
interface ItemState {
  editmds: any[];
  items: any[];
  objects: any[];
  tables: any[];
  status: {
    MetadataDrillThruItem: 'idle' | 'loading' | 'succeeded' | 'failed';
    GetDrillThruColumns: 'idle' | 'loading' | 'succeeded' | 'failed';
    getallmeasures: 'idle' | 'loading' | 'succeeded' | 'failed';
    CreateMetadataDrillThru: 'idle' | 'loading' | 'succeeded' | 'failed';
    EditMetadataDrillThru: 'idle' | 'loading' | 'succeeded' | 'failed';
    getMetadataDrillThruToolById: 'idle' | 'loading' | 'succeeded' | 'failed';
    fetchObjects: 'idle' | 'loading' | 'succeeded' | 'failed';
    fetchtables: 'idle' | 'loading' | 'succeeded' | 'failed';
    fetchcolumns: 'idle' | 'loading' | 'succeeded' | 'failed';
  };
  error: {
    MetadataDrillThruItem: string | null;
    GetDrillThruColumns: string | null;
    getallmeasures: string | null;
    CreateMetadataDrillThru: string | null;
    EditMetadataDrillThru: string | null;
    getMetadataDrillThruToolById: string | null;
    fetchObjects: string | null;
    fetchtables: string | null;
    fetchcolumns: string | null;
  };
}

// Initialize the state
const initialState: ItemState = {
  editmds: [],
  items: [],
  tables: [],
  objects: [],
  status: {
    MetadataDrillThruItem: 'idle',
    GetDrillThruColumns: 'idle',
    getallmeasures: 'idle',
    CreateMetadataDrillThru: 'idle',
    EditMetadataDrillThru: 'idle',
    getMetadataDrillThruToolById: 'idle',
    fetchObjects: 'idle',
    fetchtables: 'idle',
    fetchcolumns: 'idle',
  },
  error: {
    MetadataDrillThruItem: null,
    GetDrillThruColumns: null,
    getallmeasures: null,
    CreateMetadataDrillThru: null,
    EditMetadataDrillThru: null,
    getMetadataDrillThruToolById: null,
    fetchObjects: null,
    fetchtables: null,
    fetchcolumns: null,
  },
};

// Thunks

// 1. Fetch Metadata Drill Thru Items
export const MetadataDrillThruItem = createAsyncThunk(
  'items/MetadataDrillThruItem',
  async (MdsName: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${getBaseUrl()}/API/v3/MetadataDrillThru/Search`, {
        params: { MdsName },
      });
      return response.data.data.mdsTable;
    } catch (err: any) {
      // Capture and return the error message
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 2. Get Drill Thru Columns
export const GetDrillThruColumns = createAsyncThunk(
  'items/GetDrillThruColumns',
  async (conditionObject: object, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${getBaseUrl()}/API/v3/MetadataDrillThruTool/all`, conditionObject);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 3. Get All Measures
export const getallmeasures = createAsyncThunk(
  'items/getallmeasures',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${getBaseUrl()}/API/v3/MetadataDrillThruTool/metadatas`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 4. Create Metadata Drill Thru
export const CreateMetadataDrillThru = createAsyncThunk(
  'items/CreateMetadataDrillThru',
  async (data: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${getBaseUrl()}/API/v3/MetadataDrillThruTool`, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 5. Edit Metadata Drill Thru
export const EditMetadataDrillThru = createAsyncThunk(
  'items/EditMetadataDrillThru',
  async (data: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${getBaseUrl()}/API/v3/MetadataDrillThruTool`, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 6. Fetch Metadata Drill Thru Tool By ID
export const getMetadataDrillThruToolById = createAsyncThunk(
  'items/getMetadataDrillThruToolById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${getBaseUrl()}/API/v3/MetadataDrillThruTool/${id}`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 7. Fetch Objects
export const fetchObjects = createAsyncThunk(
  'items/fetchObjects',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${getBaseUrl()}/API/v3/Object`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 8. Fetch Tables for Custom Column
export const fetchtables = createAsyncThunk(
  'items/fetchtables',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${getBaseUrl()}/API/v3/MetadataDrillThruTool/custom/`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 9. Fetch Columns for Custom Column
export const fetchcolumns = createAsyncThunk(
  'items/fetchcolumns',
  async (table: string, { rejectWithValue }) => {
    try {
      const url = `${getBaseUrl()}/API/v3/MetadataDrillThruTool/custom?table=${table}`;
      const response = await axios.post(url, null);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Slice
const itemSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    // Add synchronous reducers here if needed
  },
  extraReducers: (builder) => {
    // 1. MetadataDrillThruItem
    builder
      .addCase(MetadataDrillThruItem.pending, (state) => {
        state.status.MetadataDrillThruItem = 'loading';
        state.error.MetadataDrillThruItem = null;
      })
      .addCase(MetadataDrillThruItem.fulfilled, (state, action) => {
        state.status.MetadataDrillThruItem = 'succeeded';
        state.items = action.payload;
      })
      .addCase(MetadataDrillThruItem.rejected, (state, action) => {
        state.status.MetadataDrillThruItem = 'failed';
        state.error.MetadataDrillThruItem = action.payload as string || 'Please enter a valid query in the box';
      });

    // 2. GetDrillThruColumns
    builder
      .addCase(GetDrillThruColumns.pending, (state) => {
        state.status.GetDrillThruColumns = 'loading';
        state.error.GetDrillThruColumns = null;
      })
      .addCase(GetDrillThruColumns.fulfilled, (state, action) => {
        state.status.GetDrillThruColumns = 'succeeded';
        state.items = action.payload; // Ensure this is intended
      })
      .addCase(GetDrillThruColumns.rejected, (state, action) => {
        state.status.GetDrillThruColumns = 'failed';
        state.error.GetDrillThruColumns = action.payload as string || 'Please enter a valid query in the box';
      });

    // 3. getallmeasures
    builder
      .addCase(getallmeasures.pending, (state) => {
        state.status.getallmeasures = 'loading';
        state.error.getallmeasures = null;
      })
      .addCase(getallmeasures.fulfilled, (state, action) => {
        state.status.getallmeasures = 'succeeded';
        state.items = action.payload; // Ensure this is intended
      })
      .addCase(getallmeasures.rejected, (state, action) => {
        state.status.getallmeasures = 'failed';
        state.error.getallmeasures = action.payload as string || 'Please enter a valid query in the box';
      });

    // 4. CreateMetadataDrillThru
    builder
      .addCase(CreateMetadataDrillThru.pending, (state) => {
        state.status.CreateMetadataDrillThru = 'loading';
        state.error.CreateMetadataDrillThru = null;
      })
      .addCase(CreateMetadataDrillThru.fulfilled, (state, action) => {
        state.status.CreateMetadataDrillThru = 'succeeded';
        state.items.push(action.payload); // Assuming the API returns the created item
      })
      .addCase(CreateMetadataDrillThru.rejected, (state, action) => {
        state.status.CreateMetadataDrillThru = 'failed';
        state.error.CreateMetadataDrillThru = action.payload as string || 'Failed to post Metadata Drill Thru';
      });

    // 5. EditMetadataDrillThru
    builder
      .addCase(EditMetadataDrillThru.pending, (state) => {
        state.status.EditMetadataDrillThru = 'loading';
        state.error.EditMetadataDrillThru = null;
      })
      .addCase(EditMetadataDrillThru.fulfilled, (state, action) => {
        state.status.EditMetadataDrillThru = 'succeeded';
        // Update the item in the items array
        if (Array.isArray(state.items) && action.payload.id) {
          const index = state.items.findIndex(item => item.id === action.payload.id);
          if (index !== -1) {
            state.items[index] = action.payload;
          }
        }        
      })
      .addCase(EditMetadataDrillThru.rejected, (state, action) => {
        state.status.EditMetadataDrillThru = 'failed';
        state.error.EditMetadataDrillThru = action.payload as string || 'Failed to post Metadata Drill Thru';
      });

    // 6. getMetadataDrillThruToolById
    builder
      .addCase(getMetadataDrillThruToolById.pending, (state) => {
        state.status.getMetadataDrillThruToolById = 'loading';
        state.error.getMetadataDrillThruToolById = null;
      })
      .addCase(getMetadataDrillThruToolById.fulfilled, (state, action) => {
        state.status.getMetadataDrillThruToolById = 'succeeded';
        state.editmds = action.payload;
      })
      .addCase(getMetadataDrillThruToolById.rejected, (state, action) => {
        state.status.getMetadataDrillThruToolById = 'failed';
        state.error.getMetadataDrillThruToolById = action.payload as string || 'Failed to fetch Metadata DrillThru Tool by ID';
      });

    // 7. fetchObjects
    builder
      .addCase(fetchObjects.pending, (state) => {
        state.status.fetchObjects = 'loading';
        state.error.fetchObjects = null;
      })
      .addCase(fetchObjects.fulfilled, (state, action) => {
        state.status.fetchObjects = 'succeeded';
        state.objects = action.payload;
      })
      .addCase(fetchObjects.rejected, (state, action) => {
        state.status.fetchObjects = 'failed';
        state.error.fetchObjects = action.payload as string || 'Failed to fetch objects';
      });

    // 8. fetchtables
    builder
      .addCase(fetchtables.pending, (state) => {
        state.status.fetchtables = 'loading';
        state.error.fetchtables = null;
      })
      .addCase(fetchtables.fulfilled, (state, action) => {
        state.status.fetchtables = 'succeeded';
        state.tables = action.payload;
      })
      .addCase(fetchtables.rejected, (state, action) => {
        state.status.fetchtables = 'failed';
        state.error.fetchtables = action.payload as string || 'Failed to fetch custom Metadata Drill Thru';
      });

    // 9. fetchcolumns
    builder
      .addCase(fetchcolumns.pending, (state) => {
        state.status.fetchcolumns = 'loading';
        state.error.fetchcolumns = null;
      })
      .addCase(fetchcolumns.fulfilled, (state, action) => {
        state.status.fetchcolumns = 'succeeded';
        state.tables = action.payload; // Ensure this is intended
      })
      .addCase(fetchcolumns.rejected, (state, action) => {
        state.status.fetchcolumns = 'failed';
        state.error.fetchcolumns = action.payload as string || 'Failed to fetch custom Metadata Drill Thru';
      });
  },
});

export default itemSlice.reducer;
